import { z } from 'zod';
import { validateDomain } from '../../../../schemas/domain.schema';

export const normalizeBusinessWebsiteDomain = (value: string): string => {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return '';

  try {
    const urlString =
      trimmed.startsWith('http://') || trimmed.startsWith('https://')
        ? trimmed
        : `https://${trimmed}`;
    return new URL(urlString).hostname.replace(/^www\./, '');
  } catch {
    return trimmed;
  }
};

// Stage 1: Organization Setup
export const organizationSetupSchema = z.object({
  org_name: z.string().min(1, 'Company/Agent name is required'),
  client_address: z.object({
    street: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(1, 'ZIP code is required'),
    country: z.enum(['US', 'Canada']),
  }),
  org_phone: z.string().min(1, 'Phone number is required'),
  email: z.string().email('Valid email is required'),
  notification_email: z.string().email('Valid notification email is required'),
  timezone: z.string().min(1, 'Timezone is required'),
});

export type OrganizationSetupData = z.infer<typeof organizationSetupSchema>;

// Stage 2: Website Branding Schema
export const websiteBrandingSchema = z
  .object({
    // Website Type Selection
    is_primary_website: z.enum(['yes', 'no'], {
      required_error: 'Please select an option',
    }),

    // Domain Setup
    domain_name: z.string().optional(),
    use_existing_domain: z.boolean().optional(),
    domain_ownership: z.enum(['own', 'purchase']).optional(),

    // Domain Access (if they own it)
    needs_access_help: z.boolean().optional(), // Track if user needs help with domain access
    uses_godaddy: z.boolean().optional(), // Track if user uses GoDaddy (true) or other provider (false)
    godaddy_access_shared: z.boolean().optional(), // Track if user has shared GoDaddy access (required when uses_godaddy is true)
    domain_registrar: z.string().optional(),
    registrar_username: z.string().optional(),
    registrar_password: z.string().optional(),
    registrar_email: z.string().email().optional().or(z.literal('')),
    registrar_phone: z.string().optional(),

    // Content Source
    content_source: z.enum(['new', 'existing']).optional(),
    existing_website_url: z.string().url().optional().or(z.literal('')),

    // NEW Content Fields
    bio_about: z.string().optional(),
    team_roster: z.string().optional(), // Can be JSON string or formatted text
    featured_locations: z.string().optional(),

    // Reviews
    reviews_link: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === '') return true; // Allow empty
          // Allow URLs with or without protocol
          try {
            const urlString =
              val.startsWith('http://') || val.startsWith('https://') ? val : `https://${val}`;
            new URL(urlString);
            return true;
          } catch {
            return false;
          }
        },
        { message: 'Invalid url (e.g., sample.com or https://sample.com)' }
      ),
    manual_testimonials: z
      .array(
        z.object({
          text: z.string(),
          author: z.string(),
        })
      )
      .optional(),

    // Key Stats
    total_sales_volume: z.string().optional(),
    homes_sold: z.string().optional(),
    market_ranking: z.string().optional(),
    awards_certifications: z.string().optional(),

    // Social Links
    facebook_url: z.string().url().optional().or(z.literal('')),
    instagram_url: z.string().url().optional().or(z.literal('')),
    youtube_url: z.string().url().optional().or(z.literal('')),
    linkedin_url: z.string().url().optional().or(z.literal('')),
    tiktok_url: z.string().url().optional().or(z.literal('')),
    twitter_url: z.string().url().optional().or(z.literal('')),
    other_social_url: z.string().url().optional().or(z.literal('')),

    // Design Contact
    design_update_emails: z.array(z.string().email()).optional(),

    // File uploads will be handled separately via Firebase Storage
    uploaded_files: z.array(z.string()).optional(), // Array of file URLs
  })
  .superRefine((data, ctx) => {
    // If creating a website (primary=yes), domain fields are required
    const isCreatingWebsite = data.is_primary_website === 'yes';

    if (isCreatingWebsite) {
      // Domain name is required and should be valid format
      if (!data.domain_name || data.domain_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Domain name is required',
          path: ['domain_name'],
        });
      } else {
        // Use shared domain validation (validates format, no http://, no paths, etc.)
        const validationResult = validateDomain(data.domain_name.trim());
        if (!validationResult.isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              validationResult.error || 'Please enter a valid domain name (e.g., yourcompany.com)',
            path: ['domain_name'],
          });
        }
      }

      // Domain ownership selection is required
      if (!data.domain_ownership) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select domain ownership option',
          path: ['domain_ownership'],
        });
      }

      // If they own the domain, they must answer if they need access help
      if (data.domain_ownership === 'own' && data.needs_access_help === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select an option',
          path: ['needs_access_help'],
        });
      }

      // If they own domain and selected "GoDaddy", godaddy_access_shared is required
      if (data.domain_ownership === 'own' && data.uses_godaddy === true) {
        if (!data.godaddy_access_shared) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please confirm you have shared access with Speculo',
            path: ['godaddy_access_shared'],
          });
        }
      }

      // If they own domain and selected "other provider", all registrar fields are required
      if (data.domain_ownership === 'own' && data.uses_godaddy === false) {
        if (!data.domain_registrar || data.domain_registrar.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Domain registrar is required',
            path: ['domain_registrar'],
          });
        }

        if (!data.registrar_username || data.registrar_username.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Registrar username is required',
            path: ['registrar_username'],
          });
        }

        if (!data.registrar_password || data.registrar_password.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Registrar password is required',
            path: ['registrar_password'],
          });
        }

        if (!data.registrar_email || data.registrar_email.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Account owner email is required',
            path: ['registrar_email'],
          });
        } else if (data.registrar_email) {
          // Validate email format
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(data.registrar_email)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid email address',
              path: ['registrar_email'],
            });
          }
        }

        if (!data.registrar_phone || data.registrar_phone.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Account owner phone is required',
            path: ['registrar_phone'],
          });
        }
      }
    }
  });

export type WebsiteBrandingData = z.infer<typeof websiteBrandingSchema>;

// Stage 3: Remi Setup Schema (formerly Stage 2)
export const remiSetupSchema = z.object({
  // Voice Setup
  remi_intro_name: z.string().min(1, 'Introduction name is required'),
  assistant_name: z.string().min(1, 'Assistant name is required'),
  assistant_name_pronunciation: z.string().min(1, 'Pronunciation is required'),
  voice: z.string().min(1, 'Voice selection is required'),
  background_track: z.string().optional(),

  // Knowledge Base Setup
  kb_name: z.string().min(1, 'Knowledge base name is required'),
  service_type: z.string().min(1, 'Service type is required'),
  service_areas: z.string().min(1, 'Service areas are required'),
  why_work_with_you: z.string().min(1, 'This field is required'),
  website_url: z.string().min(1, 'Website URL is required'),
  custom_questions: z
    .array(
      z.object({
        question: z.string(),
        answer: z.string(),
      })
    )
    .optional(),
});

export type RemiSetupData = z.infer<typeof remiSetupSchema>;

// Stage 4: Twilio/Phone Number Setup Schema (formerly Stage 3)
export const twilioSetupSchema = z
  .object({
    // Phone Number Preferences
    preferred_area_code: z.string().min(3, 'Area code is required'),
    preferred_location: z.string().min(1, 'Preferred location is required'),
    other_area_codes: z.array(z.string()).optional(),
    area_code_acknowledgement: z.boolean().optional(),

    // Business Verification
    legal_business_name: z.string().min(1, 'Legal business name is required'),
    business_address: z.object({
      street: z.string().min(1, 'Street address is required'),
      city: z.string().min(1, 'City is required'),
      state: z.string().min(1, 'State is required'),
      zip: z.string().min(1, 'ZIP code is required'),
      country: z.enum(['US', 'Canada']),
    }),
    business_type: z.enum(['LLC', 'Corporation', 'Sole Proprietorship']),
    ein_cbn: z.string().min(1, 'EIN/CBN is required'),
    business_website: z
      .string()
      .min(1, 'Business website is required')
      .transform(normalizeBusinessWebsiteDomain)
      .refine(
        (val) => {
          const validationResult = validateDomain(val);
          return validationResult.isValid;
        },
        {
          message:
            'Please enter a valid business website (e.g., yourcompany.com or https://yourcompany.com)',
        }
      ),
  })
  .superRefine((data, ctx) => {
    // If other_area_codes has values, area_code_acknowledgement must be true
    if (data.other_area_codes && data.other_area_codes.length > 0) {
      if (!data.area_code_acknowledgement) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please acknowledge understanding of alternative area codes',
          path: ['area_code_acknowledgement'],
        });
      }
    }
  });

export type TwilioSetupData = z.infer<typeof twilioSetupSchema>;

// Stage 4: IDX Licensing Agreement & MLS Approval (Step 1 - MLS)
export const mlsEntrySchema = z.object({
  mls_name: z.string().min(1, 'MLS is required'),
  brokerage_name: z.string().min(1, 'Brokerage name is required'),
  broker_full_name: z.string().min(1, 'Broker full name is required'),
  broker_email: z.string().email('Valid broker email is required'),
  agent_name: z.string().min(1, 'Agent name is required'),
  agent_email: z.string().email('Valid agent email is required'),
  agent_mls_id: z.string().min(1, 'Agent MLS ID is required'),
});

export const mlsSetupSchema = z.object({
  mls_entries: z.array(mlsEntrySchema).min(1, 'At least one MLS entry is required'),
});

export type MLSSetupData = z.infer<typeof mlsSetupSchema>;

// Stage 4: IDX Site Setup (Step 2 - Site Configuration)
export const idxSiteSetupSchema = z
  .object({
    // Website type from Stage 2
    has_website: z.enum(['primary', 'secondary', 'no']),

    // Domain configuration
    idx_subdomain: z.string().optional(),
    new_domain: z.string().optional(),
    website_domain: z.string().optional(), // Pulled from Stage 2

    // Branding
    logo_url: z.string().min(1, 'Logo is required'),
    primary_color: z.string().optional(),
    secondary_color: z.string().optional(),

    // Compliance links
    privacy_policy_url: z.string().url().optional().or(z.literal('')),
    terms_of_service_url: z.string().url().optional().or(z.literal('')),
    disclaimer_url: z.string().url().optional().or(z.literal('')),

    // Domain access help (if needed)
    needs_domain_help: z.boolean().optional(),
    domain_access_shared: z.boolean().optional(), // Track if user already shared domain access
    domain_registrar: z.string().optional(),
    registrar_username: z.string().optional(),
    registrar_password: z.string().optional(),
    registrar_email: z.string().email().optional().or(z.literal('')),
    registrar_phone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Subdomain validation regex: lowercase letters, numbers, and hyphens only
    // Cannot start or end with hyphen, no consecutive hyphens
    const subdomainPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

    // Validation based on website type
    if (data.has_website === 'primary' || data.has_website === 'secondary') {
      // Subdomain is required
      if (!data.idx_subdomain || data.idx_subdomain.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'IDX subdomain is required',
          path: ['idx_subdomain'],
        });
      } else if (!subdomainPattern.test(data.idx_subdomain.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Subdomain must contain only lowercase letters, numbers, and hyphens (e.g., "homes" or "property-search")',
          path: ['idx_subdomain'],
        });
      }
    }

    if (data.has_website === 'no') {
      // New domain is required and must be valid format
      if (!data.new_domain || data.new_domain.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Domain is required',
          path: ['new_domain'],
        });
      } else {
        // Use shared domain validation (validates format, no http://, no paths, etc.)
        const validationResult = validateDomain(data.new_domain.trim());
        if (!validationResult.isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              validationResult.error || 'Please enter a valid domain name (e.g., yourcompany.com)',
            path: ['new_domain'],
          });
        }
      }
      // Subdomain for new domain is required
      if (!data.idx_subdomain || data.idx_subdomain.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'IDX subdomain is required',
          path: ['idx_subdomain'],
        });
      } else if (!subdomainPattern.test(data.idx_subdomain.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Subdomain must contain only lowercase letters, numbers, and hyphens (e.g., "homes" or "property-search")',
          path: ['idx_subdomain'],
        });
      }
    }

    // If domain_access_shared is true, skip registrar validation (user delegated access or Speculo purchased domain)
    // If domain help is needed AND domain access has NOT been shared, registrar fields are required
    if (data.needs_domain_help && !data.domain_access_shared) {
      if (!data.domain_registrar || data.domain_registrar.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Domain registrar is required',
          path: ['domain_registrar'],
        });
      }
      if (!data.registrar_username || data.registrar_username.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Username is required',
          path: ['registrar_username'],
        });
      }
      if (!data.registrar_password || data.registrar_password.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Password is required',
          path: ['registrar_password'],
        });
      }
      if (!data.registrar_email || data.registrar_email.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Email is required',
          path: ['registrar_email'],
        });
      }
      if (!data.registrar_phone || data.registrar_phone.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone is required',
          path: ['registrar_phone'],
        });
      }
    }
    // If domain_access_shared is true, registrar fields are NOT required (user already delegated access or Speculo purchased)
  });

export type IDXSiteSetupData = z.infer<typeof idxSiteSetupSchema>;

// Combined IDX data (both steps)
export const idxSetupSchema = z.object({
  mls_data: mlsSetupSchema,
  site_data: idxSiteSetupSchema.optional(),
});

export type IDXSetupData = z.infer<typeof idxSetupSchema>;

// Stage 5: CRM Setup Schema
export const CRM_OPTIONS = [
  { value: 'fub', label: 'Follow Up Boss' },
  { value: 'cinc', label: 'CINC' },
  { value: 'brivity', label: 'Brivity' },
  { value: 'claritie_ops', label: 'Claritie Ops' },
  { value: 'lofty', label: 'Lofty' },
  { value: 'command', label: 'Command' },
  { value: 'boomtown', label: 'Boomtown' },
  { value: 'gohighlevel', label: 'GoHighLevel' },
  { value: 'realgeeks', label: 'Real Geeks' },
  { value: 'boldtrail', label: 'Bold Trail/KV Core' },
  { value: 'compass', label: 'Compass' },
  { value: 'other', label: 'Other (enter below)' },
] as const;

// Single CRM entry schema
export const crmEntrySchema = z
  .object({
    // CRM Selection
    crm_type: z.string().min(1, 'Please select your CRM'),
    crm_other_name: z.string().optional(),
    ai_disclaimer_acknowledged: z.boolean().optional(),

    // API Key (if available)
    super_admin_api_key: z.string().optional(),

    // Fallback credentials (if API key not available)
    login_url: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === '') return true;
          try {
            const urlString =
              val.startsWith('http://') || val.startsWith('https://') ? val : `https://${val}`;
            new URL(urlString);
            return true;
          } catch {
            return false;
          }
        },
        { message: 'Please enter a valid URL' }
      ),
    super_admin_username: z.string().optional(),
    super_admin_password: z.string().optional(),
    two_fa_contact: z.string().optional(),

    // Brivity-specific fields
    brivity_website_login_url: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === '') return true;
          try {
            const urlString =
              val.startsWith('http://') || val.startsWith('https://') ? val : `https://${val}`;
            new URL(urlString);
            return true;
          } catch {
            return false;
          }
        },
        { message: 'Please enter a valid URL' }
      ),
    brivity_username: z.string().optional(),
    brivity_password: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.crm_type && !data.ai_disclaimer_acknowledged) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please confirm the AI CRM integration acknowledgment before continuing',
        path: ['ai_disclaimer_acknowledged'],
      });
    }

    // If CRM is "other", require the other name
    if (data.crm_type === 'other') {
      if (!data.crm_other_name || data.crm_other_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter your CRM name',
          path: ['crm_other_name'],
        });
      }
    }

    // FUB, CINC, and Brivity connect via Rollout OAuth — no manual credentials collected.
    // For any other non-FUB CRM, require either an API key OR all fallback credentials.
    const rolloutCrms = new Set(['fub', 'cinc', 'brivity']);
    if (data.crm_type && !rolloutCrms.has(data.crm_type)) {
      const hasApiKey = data.super_admin_api_key && data.super_admin_api_key.trim() !== '';

      if (!hasApiKey) {
        if (!data.login_url || data.login_url.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Login URL is required when API key is not provided',
            path: ['login_url'],
          });
        }
        if (!data.super_admin_username || data.super_admin_username.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Account owner username is required when API key is not provided',
            path: ['super_admin_username'],
          });
        }
        if (!data.super_admin_password || data.super_admin_password.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Account owner password is required when API key is not provided',
            path: ['super_admin_password'],
          });
        }
        if (!data.two_fa_contact || data.two_fa_contact.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '2FA contact is required when API key is not provided',
            path: ['two_fa_contact'],
          });
        }
      }
    }
  });

export type CRMEntryData = z.infer<typeof crmEntrySchema>;

// Multiple CRMs setup schema
export const crmSetupSchema = z.object({
  crm_entries: z.array(crmEntrySchema).min(1, 'At least one CRM is required'),
});

export type CRMSetupData = z.infer<typeof crmSetupSchema>;
